import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { supabase } from '../utils/supabase';
import { Product, CartItem, Category } from '../types';
import { useNavigate } from 'react-router-dom';
import { buildCategoryTree, getCategoryFilterIds, findParentCategoryId } from '../utils/categoryHelpers';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categoryTree, setCategoryTree] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<number | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [quantities, setQuantities] = useState<{[key: number]: number}>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const { user, signOut } = useAuth();
  const { language, setLanguage, isRTL } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCartItems();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, selectedCategory, selectedSubCategory]);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('habib_categories')
      .select('*')
      .order('name', { ascending: true});
    
    if (error) {
      console.error('Error fetching categories:', error);
    } else if (data) {
      setAllCategories(data);
      setCategoryTree(buildCategoryTree(data));
    }
  };

  const fetchProducts = async () => {
    const { data, error } = await supabase
      .from('habib_products')
      .select('*, category:habib_categories(id, name)')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching products:', error);
    } else if (data) {
      setProducts(data);
      setFilteredProducts(data);
    }
  };

  const filterProducts = () => {
    let filtered = products.filter(product => {
      const matchesSearch = product.nama_produk.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.deskripsi_produk.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesCategory = true;
      if (selectedSubCategory !== null) {
        // Exact sub-category match
        matchesCategory = product.category_id === selectedSubCategory;
      } else if (selectedCategory !== null) {
        // Main category + all its sub-categories
        const filterIds = getCategoryFilterIds(selectedCategory, allCategories);
        matchesCategory = product.category_id != null && filterIds.includes(product.category_id);
      }
      
      return matchesSearch && matchesCategory;
    });
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  const fetchCartItems = async () => {
    const { data, error } = await supabase
      .from('habib_cart')
      .select(`
        *,
        product:habib_products(*),
        menu:habib_menu(*)
      `)
      .eq('user_id', user?.id);
    
    if (error) {
      console.error('Error fetching cart:', error);
    } else if (data) {
      setCartItems(data);
    }
  };

  const updateQuantity = async (productId: number, newQuantity: number) => {
    setQuantities(prev => ({ ...prev, [productId]: newQuantity }));
    
    if (newQuantity === 0) {
      const existingItem = cartItems.find(item => item.product_id === productId);
      if (existingItem) {
        const { error } = await supabase.from('habib_cart').delete().eq('id', existingItem.id);
        if (error) {
          alert('Failed to remove item: ' + error.message);
          return;
        }
        setCartItems(cartItems.filter(item => item.id !== existingItem.id));
      }
      return;
    }
    
    const existingItem = cartItems.find(item => item.product_id === productId);
    
    if (existingItem) {
      const { data, error } = await supabase
        .from('habib_cart')
        .update({ quantity: newQuantity })
        .eq('id', existingItem.id)
        .select('*, product:habib_products(*)');
      
      if (error) {
        alert('Failed to update cart: ' + error.message);
        return;
      }
      
      if (data) {
        setCartItems(cartItems.map(item => 
          item.id === existingItem.id ? data[0] : item
        ));
      }
    } else {
      const { data, error } = await supabase
        .from('habib_cart')
        .insert([{ user_id: user?.id, product_id: productId, quantity: newQuantity }])
        .select('*, product:habib_products(*)');
      
      if (error) {
        alert('Failed to add to cart: ' + error.message);
        return;
      }
      
      if (data) {
        setCartItems([...cartItems, ...data]);
      }
    }
  };
  
  const getQuantity = (productId: number) => {
    return quantities[productId] || cartItems.find(item => item.product_id === productId)?.quantity || 0;
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className="mr-4 p-2 hover:bg-green-50 rounded-xl transition-all"
                style={{color: '#05472A'}}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center shadow-lg overflow-hidden`} style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('guestServingProduct')}</h1>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-4`}>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'en' ? {backgroundColor: '#05472A'} : {}}
                >
                  EN
                </button>
                <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'ar' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'ar' ? {backgroundColor: '#05472A'} : {}}
                >
                  AR
                </button>
              </div>
              <span className="hidden md:block text-sm font-medium" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8 pb-24">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={language === 'en' ? 'Search products...' : 'البحث عن المنتجات...'}
              className="w-full px-4 py-3 pl-12 pr-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Categories */}
        {categoryTree.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => { setSelectedCategory(null); setSelectedSubCategory(null); }}
                className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === null
                    ? 'text-white shadow-soft'
                    : 'bg-white border hover:bg-green-50'
                }`}
                style={selectedCategory === null ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
              >
                {language === 'en' ? 'All Products' : 'جميع المنتجات'}
              </button>
              {categoryTree.map((category) => (
                <button
                  key={category.id}
                  onClick={() => { setSelectedCategory(category.id); setSelectedSubCategory(null); }}
                  className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'text-white shadow-soft'
                      : 'bg-white border hover:bg-green-50'
                  }`}
                  style={selectedCategory === category.id ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Sub-category filter row */}
            {selectedCategory !== null && (() => {
              const selectedMain = categoryTree.find(c => c.id === selectedCategory);
              const subs = selectedMain?.subcategories || [];
              if (subs.length === 0) return null;
              return (
                <div className="flex items-center gap-2 overflow-x-auto pt-3 pb-1 scrollbar-hide">
                  <button
                    onClick={() => setSelectedSubCategory(null)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                      selectedSubCategory === null
                        ? 'text-white'
                        : 'bg-white border hover:bg-green-50'
                    }`}
                    style={selectedSubCategory === null ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
                  >
                    {language === 'en' ? 'All' : 'الكل'}
                  </button>
                  {subs.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setSelectedSubCategory(sub.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                        selectedSubCategory === sub.id
                          ? 'text-white'
                          : 'bg-white border hover:bg-green-50'
                      }`}
                      style={selectedSubCategory === sub.id ? {backgroundColor: '#05472A'} : {borderColor: '#05472A40', color: '#05472A'}}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {currentProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden group hover:scale-105 border cursor-pointer" style={{borderColor: '#05472A20'}}>
              <div className="relative overflow-hidden" onClick={() => navigate(`/product/${product.id}`)}>
                <img 
                  src={product.photo} 
                  alt={product.nama_produk}
                  className="w-full h-40 sm:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'}`}>
                  <span className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-sm sm:text-base font-bold transition-all duration-300 ${
                    product.stock > 0 
                      ? 'bg-green-100 border' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`} style={product.stock > 0 ? {color: '#05472A', borderColor: '#05472A40'} : {}}>
                    {product.stock > 0 ? `${product.stock} ${t('left')}` : `${t('outOfStock')}`}
                  </span>
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <div onClick={() => navigate(`/product/${product.id}`)}>
                  <h3 className="font-bold mb-2 text-sm sm:text-base line-clamp-2" style={{color: '#05472A'}}>{product.nama_produk}</h3>
                  {product.category && (() => {
                    const parentId = product.category_id ? findParentCategoryId(product.category_id, allCategories) : null;
                    const parentCat = parentId ? allCategories.find(c => c.id === parentId) : null;
                    return (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {parentCat && (
                          <span className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold" style={{backgroundColor: '#05472A15', color: '#05472A'}}>
                            {parentCat.name}
                          </span>
                        )}
                        <span className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold" style={{backgroundColor: '#05472A20', color: '#05472A'}}>
                          {product.category.name}
                        </span>
                      </div>
                    );
                  })()}
                  <p className="text-xs sm:text-sm mb-4 line-clamp-2" style={{color: '#05472A99'}}>{product.deskripsi_produk}</p>
                </div>
                {product.stock > 0 ? (
                  <div className="flex items-center justify-center gap-3" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={() => updateQuantity(product.id, Math.max(0, getQuantity(product.id) - 1))}
                      className="w-10 h-10 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                      style={{borderColor: '#05472A40', color: '#05472A'}}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <span className="text-xl font-semibold w-8 text-center" style={{color: '#05472A'}}>
                      {getQuantity(product.id)}
                    </span>
                    <button 
                      onClick={() => updateQuantity(product.id, Math.min(product.stock, getQuantity(product.id) + 1))}
                      className="w-10 h-10 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                      style={{borderColor: '#05472A40', color: '#05472A'}}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-white border border-slate-200 text-slate-700 text-center">
                    {t('outOfStock')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
              style={{borderColor: '#05472A40', color: '#05472A'}}
            >
              ←
            </button>
            <span className="px-4 py-2 font-medium" style={{color: '#05472A'}}>
              {t('page')} {currentPage} {t('of')} {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
              style={{borderColor: '#05472A40', color: '#05472A'}}
            >
              →
            </button>
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button 
          onClick={() => navigate('/cart')} 
          className="text-white px-8 py-4 rounded-full shadow-strong hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center gap-4 min-w-[200px] justify-center"
          style={{backgroundColor: '#05472A'}}
        >
          <div className="relative">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m4 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
            </svg>
            {cartItems.reduce((sum, item) => sum + item.quantity, 0) > 0 && (
              <span className={`absolute -top-2 ${isRTL ? '-left-2' : '-right-2'} bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse`}>
                {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </div>
          <span className="font-semibold text-lg">{t('viewCart')}</span>
        </button>
      </div>
    </div>
  );
};

export default Products;
